import { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";

/**
 * PriceGate — soft privacy toggle for pricing content on airline-private slides.
 *
 * Route-level authentication (`/pitch/:code` + JWT cookie) already gates airline
 * data server-side. This component keeps a lightweight client-side "Show / Hide
 * Prices" toggle so the numbers are not exposed the instant you land on the
 * slide (e.g. mid-meeting share-screen). No password is required — the user is
 * already authorised.
 *
 * Props:
 *  - accentColor: brand colour for the toggle
 *  - isDark: theme flag
 *  - children: (revealed, Toggle, lockedNode) => JSX
 */
const PriceGate = ({ accentColor = "#FFD700", isDark, children, revealed: controlled, onToggle }) => {
  const [local, setLocal] = useState(false);
  const isControlled = typeof controlled === "boolean";
  const revealed = isControlled ? controlled : local;
  const setRevealed = isControlled ? () => onToggle?.() : setLocal;

  const Toggle = (
    <button
      type="button"
      onClick={() => setRevealed((v) => !v)}
      data-testid="price-toggle"
      aria-pressed={revealed}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
        isDark
          ? "bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
          : "bg-slate-200/80 border border-slate-300 text-slate-600 hover:bg-slate-300 hover:text-slate-900"
      }`}
    >
      {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      {revealed ? "Hide Prices" : "Show Prices"}
    </button>
  );

  return typeof children === "function"
    ? children({ revealed, Toggle, lockedNode: defaultLocked(isDark, accentColor, isControlled) })
    : null;
};

const defaultLocked = (isDark, accentColor, neutralHint) => (
  <div className={`flex flex-col items-center justify-center text-center rounded-xl py-16 px-6 border ${
    isDark ? "bg-slate-800/30 border-slate-700/40" : "bg-slate-50 border-slate-200"
  }`}>
    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${accentColor}20` }}>
      <Lock className="w-6 h-6" style={{ color: accentColor }} />
    </div>
    <div className={`text-base font-bold mb-1 ${isDark ? "text-white" : "text-slate-900"}`}>Pricing is hidden</div>
    <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
      Click <span className="font-semibold">Show Prices</span>{neutralHint ? "" : " in the top right"} to reveal the numbers.
    </p>
  </div>
);

export default PriceGate;
